import { Linkedin } from "lucide-react";
import { AboutReveal, AboutStagger, AboutStaggerItem } from "./about-reveal";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { teamCopy } from "@/lib/aboutPageCopy";

type LeaderMeta = {
  name: string;
  role: string;
  image: string;
  linkedin: string;
};

const leadersMeta: LeaderMeta[] = [
  {
    name: "Omkar Biradar",
    role: "CEO",
    image:
      "https://res.cloudinary.com/zeqof4ic/image/upload/v1783853257/omkarddcimage_lupe9z.png",
    linkedin: "https://www.linkedin.com/in/omkar-biradar-457725245",
  },
  {
    name: "Amol Rakh",
    role: "CTO",
    image:
      "https://res.cloudinary.com/zeqof4ic/image/upload/v1783850830/amolddcimg2-removebg-preview_ip2raa.png",
    linkedin: "https://www.linkedin.com/in/amolrakha22",
  },
  {
    name: "Karan Lande",
    role: "COO",
    image:
      "https://res.cloudinary.com/zeqof4ic/image/upload/v1783851059/karanddcimg-removebg-preview_ofadw1.png",
    linkedin: "https://www.linkedin.com/in/karan-lande-3326a431b",
  },
];

export function Team() {
  const lang = useSiteLanguage();
  const t = teamCopy[lang];

  return (
    <section
      id="leadership"
      className="scroll-mt-24 bg-background pb-20 md:pb-28 lg:pb-32"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <AboutReveal className="mx-auto mb-12 max-w-2xl text-center md:mb-16">
          <span className="mb-4 block text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
            {t.eyebrow}
          </span>
          <h2 className="text-balance text-2xl font-bold tracking-tight text-foreground md:text-3xl lg:text-[2.15rem]">
            {t.title}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-[15px]">
            {t.subtitle}
          </p>
        </AboutReveal>

        <AboutStagger className="mx-auto flex max-w-4xl flex-wrap justify-center gap-4 lg:gap-5">
          {leadersMeta.map((leader, index) => (
            <AboutStaggerItem
              key={leader.name}
              className="w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.875rem)] lg:max-w-[17.5rem]"
            >
              <article className="group flex h-full flex-col overflow-hidden rounded-[1.25rem] border border-black/[0.06] bg-white p-4 shadow-[0_12px_36px_-20px_rgba(0,0,0,0.16)] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_20px_44px_-18px_rgba(0,0,0,0.2)] dark:border-white/10 dark:bg-white">
                <div className="relative mb-4 aspect-[4/5] w-full overflow-hidden rounded-[0.95rem] bg-[#F3F4F6]">
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="h-full w-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                  />
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/[0.06]"
                  />
                  <a
                    href={leader.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${leader.name} on LinkedIn`}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute bottom-2.5 right-2.5 z-20 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-neutral-900 shadow-sm transition-all duration-300 hover:scale-105 hover:bg-neutral-50"
                  >
                    <Linkedin className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </a>
                </div>

                <div className="flex flex-1 flex-col px-0.5">
                  <p className="mb-1 text-[10px] font-medium uppercase tracking-[0.18em] text-neutral-400">
                    {leader.role}
                  </p>
                  <h3 className="text-[15px] font-semibold tracking-tight text-neutral-950">
                    {leader.name}
                  </h3>
                  <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-neutral-500">
                    {t.bios[index]}
                  </p>
                </div>
              </article>
            </AboutStaggerItem>
          ))}
        </AboutStagger>
      </div>
    </section>
  );
}
